import axiosInstance from "./axiosInstance";
import UrlCreator from "./UrlCreator";
import {AxiosResponse} from "axios";
import {EnumModules, IProfileResource} from "../../resources/IProfile.resource";
import {ICompaniesResource} from "../../resources/ICompany.resource";
import {IEmployeeResource, IEmployeesResource} from "../../resources/IEmployee.resource";
import {ICallDetailsResource} from "../../resources/ICallDetails.resource";
import {ISignupInfo} from "../../mobx/signup/SignupObserver";
import {ICompanyBalancesResource} from "../../resources/ICompanyBalances.resource";
import {ICallResource, ICallServer} from "../../resources/ICall.resource";
import {IRequestResource} from "../../resources/IRequest.resource";
import {ChecklistResource} from "../../resources/IChecklist.resource";
import {PerformanceByIndicatorsResource, TotalScoreByEmployeeResource} from "../../resources/dashboard.resource";
import {IBillingInfoResource} from "../../resources/IBilling.resource";
import {IAuditChecklistItem, IAuditRevisionResource, IPostAuditBody, mapAuditRevisions} from "../../resources/IChecklistAudit.resource";
import {IReasoningResource} from "../../resources/IReasoning.resource";
import {
    ICallChecklistAuditSimilarityResourceServer,
    ICallChecklistAuditSimilarityResource,
    mapCallChecklistAuditSimilarity,
} from "../../resources/ICallChecklistAuditSimilarity.resource";
import {
    IChecklistManagementEditBody,
    IChecklistManagementItemEditBody,
    IChecklistManagementItemResource,
    IChecklistManagementListResource,
    IChecklistManagementResource,
} from "../../resources/IChecklistManagement.resource";

export interface IPostCallsVariables {
    companyId: number;
    employeeId: number;
    encodedPhoneNumber: string;
    phoneTime: string;
}

export interface IPostPassword {
    otp: string;
    newPassword: string;
    confirmNewPassword: string;
}

export interface IPostResetPasswordEmail {
    email?: string;
    userName?: string;
}

export interface IPatchRequest {
    companyLimits?: {
        maxTotalPhoneCalls?: number;
        maxTotalPhoneCallsDurationSec?: number;
    }
    comment?: string;
}

class FetchService {
    getProfile = async (): Promise<AxiosResponse<IProfileResource>> => {
        return await axiosInstance.get(UrlCreator.getProfile())
    }

    getMyEmployee = async (companyId: number): Promise<AxiosResponse<IEmployeeResource>> => {
        return await axiosInstance.get(UrlCreator.getMyEmployee(companyId))
    }

    getCompanies = async (): Promise<AxiosResponse<ICompaniesResource | null>> => {
        const data = await this.getProfile();
        if (data.data.modules.includes(EnumModules.LEAD_GENERATION)) {
            // @ts-ignore
            return null;
        }

        return await axiosInstance.get(UrlCreator.getCompanies())
    }

    getEmployees = async (companyId: number, page: number, searchRequest: string): Promise<AxiosResponse<IEmployeesResource>> => {
        return await axiosInstance.get(UrlCreator.getAllEmployees(companyId), {
            params: {
                pageToken: page,
                pageSize: 50,
                searchRequest: searchRequest
            }
        })
    }

    getCompanyBalance = async (companyId: number): Promise<AxiosResponse<ICompanyBalancesResource>> => {
        return await axiosInstance.get(UrlCreator.getCompanyBalance(companyId))
    };

    getAllCalls = async (companyId: number, params?: URLSearchParams): Promise<AxiosResponse<{ calls: ICallServer[], total: number }>> => {
        return await axiosInstance.get(UrlCreator.getAllCalls(companyId), { params });
    };

    getAllCallsFile = async (companyId: number, params: URLSearchParams): Promise<AxiosResponse<string>> => {
        return await axiosInstance.get(UrlCreator.getAllCallsFile(companyId), {
            params,
            headers: { accept: 'text/csv' },
        });
    };

    patchCallStatus = async (companyId: number, callId: string, status: string): Promise<AxiosResponse<ICallResource>> => {
        return await axiosInstance.patch(UrlCreator.patchCallStatus(companyId, callId, status));
    };

    patchCallBatchStatus = async (companyId: number, callIds: string[], status: string): Promise<AxiosResponse<unknown>> => {
        return await axiosInstance.patch(UrlCreator.patchCallBatchStatus(companyId, callIds, status));
    };

    getRequests = async (params: any): Promise<AxiosResponse<{ total: number, requests: IRequestResource[] }>> => {
        return await axiosInstance.get(UrlCreator.getRequests(), {params: params});
    }

    getAllChecklists = async (companyId: number): Promise<AxiosResponse<ChecklistResource[]>> => {
        return await axiosInstance.get(UrlCreator.getAllChecklists(companyId));
    };

    patchCallDecoding = async (companyId: number, callId: string): Promise<AxiosResponse<ICallDetailsResource>> => {
        return await axiosInstance.patch(UrlCreator.patchDecodingCall(companyId, callId));
    };

    patchRequestApprove = async (requestId: number, body: any): Promise<AxiosResponse<IRequestResource>> => {
        return await axiosInstance.patch(UrlCreator.patchRequestApprove(requestId), body);
    };

    patchRequestChangeStatus = async (requestId: number, status: string): Promise<AxiosResponse<IRequestResource>> => {
        return await axiosInstance.patch(UrlCreator.patchChangeStatus(requestId, status));
    };

    patchRequestEditLimits = async (requestId: number, body: IPatchRequest): Promise<AxiosResponse<IRequestResource>> => {
        return await axiosInstance.patch(UrlCreator.patchEditRequest(requestId), body);
    };

    putCallLabseg = async (companyId: number, phoneCallUuid: string, labsegId: number, labelId: number, body: any): Promise<AxiosResponse<{
        id: number,
        name: string,
        value: string
    }>> => {
        return await axiosInstance.put(UrlCreator.putCallLabseg(companyId, phoneCallUuid, labsegId, labelId), body)
    }

    putChangeMyPassword = async (oldPassword: string, newPassword: string, confirmNewPassword: string) => {
        return await axiosInstance.put(UrlCreator.putChangeMyPassword(oldPassword, newPassword, confirmNewPassword),)
    }

    patchCallNotes = async (companyId: number, phoneCallUuid: string, notes: string) => {
        return await axiosInstance.patch(UrlCreator.patchCallNotes(companyId, phoneCallUuid, notes))
    }

    postNewLead = async (body: ISignupInfo) => {
        return await axiosInstance.post(UrlCreator.postNewLead(), body);
    }

    postCalls = async (variables: IPostCallsVariables, body: FormData) => {
        const {companyId, employeeId, encodedPhoneNumber, phoneTime} = variables;
        return await axiosInstance.post(UrlCreator.postCalls(companyId, employeeId, encodedPhoneNumber, phoneTime), body, {headers: {'Content-Type': 'multipart/form-data'}});
    }

    postPassword = async (body: IPostPassword) => {
        return await axiosInstance.post(UrlCreator.postPasswordChange(), body);
    };

    postRefreshEmployees = async () => {
        return await axiosInstance.post(UrlCreator.postRefreshEmployees());
    }

    postResetPasswordEmail = async (body: IPostResetPasswordEmail) => {
        return await axiosInstance.post(UrlCreator.postPasswordResetEmail(), body);
    };

    deleteLabsegLabels = async (companyId: number, callId: string, labsegId: number, labelIds: number[]) => {
        return await axiosInstance.delete(UrlCreator.deleteCallLabels(companyId, callId, labsegId, labelIds));
    };

    performanceByIndicators = async (companyId: number, checklistId: number, params: any):
        Promise<AxiosResponse<PerformanceByIndicatorsResource[]>> => {
        return axiosInstance.get(UrlCreator.performanceByIndicators(companyId, checklistId), {params});
    };

    totalScoreByEmployee = async (companyId: number, checklistId: number, params: any):
        Promise<AxiosResponse<TotalScoreByEmployeeResource[]>> => {
        return axiosInstance.get(UrlCreator.totalScoreByEmployee(companyId, checklistId), {params});
    };

    billingInfo = async (companyId: number, year: number, month: number, timeZone?: string):
        Promise<AxiosResponse<IBillingInfoResource>> => {
        return axiosInstance.get(UrlCreator.getBillingInfo(companyId, year, month), {
            params: {
                timeZone
            }
        });
    };

    linkTelegramAccount = async (token: string):
        Promise<AxiosResponse<IBillingInfoResource>> => {
        return axiosInstance.post(UrlCreator.postLinkTelegramAccount(token));
    };

    postSignOut = async (): Promise<void> => {
        await axiosInstance.post(UrlCreator.postSignOut());
    };

    getAuditChecklists = async (companyId: number): Promise<AxiosResponse<IAuditChecklistItem[]>> =>
        axiosInstance.get(UrlCreator.getAuditChecklists(companyId), { silentOn403: true });

    getAuditData = async (companyId: number, phonecallUuid: string, checklistId: number): Promise<IAuditRevisionResource[]> => {
        const res = await axiosInstance.get(UrlCreator.getAuditData(companyId, phonecallUuid, checklistId));
        return mapAuditRevisions(res.data);
    };

    postAuditData = async (companyId: number, phonecallUuid: string, body: IPostAuditBody): Promise<IAuditRevisionResource[]> => {
        const res = await axiosInstance.post(UrlCreator.postAuditData(companyId, phonecallUuid), body);
        return mapAuditRevisions(res.data);
    };

    getItemReasoning = async (companyId: number, phonecallUuid: string, checklistId: number, itemUuid: string): Promise<AxiosResponse<IReasoningResource[]>> =>
        axiosInstance.get(UrlCreator.getItemReasoning(companyId, phonecallUuid, checklistId, itemUuid));

    postItemReasoning = async (companyId: number, phonecallUuid: string, checklistId: number, itemUuid: string, body: { reasoning: string }): Promise<AxiosResponse<IReasoningResource>> =>
        axiosInstance.post(UrlCreator.postItemReasoning(companyId, phonecallUuid, checklistId, itemUuid), body);

    putItemReasoning = async (companyId: number, phonecallUuid: string, checklistId: number, itemUuid: string, reasoningId: number, body: { reasoning: string }): Promise<AxiosResponse<IReasoningResource>> =>
        axiosInstance.put(UrlCreator.putItemReasoning(companyId, phonecallUuid, checklistId, itemUuid, reasoningId), body);

    deleteItemReasoning = async (companyId: number, phonecallUuid: string, checklistId: number, itemUuid: string, reasoningId: number): Promise<AxiosResponse<void>> =>
        axiosInstance.delete(UrlCreator.deleteItemReasoning(companyId, phonecallUuid, checklistId, itemUuid, reasoningId));

    getChecklistAuditSimilarity = async (
        companyId: number,
        checklistId: number,
        params: URLSearchParams,
    ): Promise<{ calls: ICallChecklistAuditSimilarityResource[]; total: number }> => {
        const res = await axiosInstance.get(
            UrlCreator.getChecklistAuditSimilarity(companyId, checklistId),
            { params },
        );
        const data = res.data as { calls: ICallChecklistAuditSimilarityResourceServer[]; total: number };
        return {
            calls: mapCallChecklistAuditSimilarity(data.calls),
            total: data.total,
        };
    };

    getChecklistAuditColumnStats = async (
        companyId: number,
        checklistId: number,
        params: URLSearchParams,
    ): Promise<Record<string, number>> => {
        const res = await axiosInstance.get<Record<string, number>>(
            UrlCreator.getChecklistAuditColumnStats(companyId, checklistId),
            { params },
        );
        return res.data;
    };

    getChecklistAuditSimilarityCsv = async (
        companyId: number,
        checklistId: number,
        params: URLSearchParams,
    ): Promise<AxiosResponse<string>> =>
        axiosInstance.get(UrlCreator.getChecklistAuditSimilarityCsv(companyId, checklistId), {
            params,
            headers: { accept: 'text/csv' },
        });

    getChecklistManagementList = async (
        companyId: number,
        params: { pageSize?: number; pageToken?: string; searchRequest?: string },
    ): Promise<AxiosResponse<IChecklistManagementListResource>> =>
        axiosInstance.get(UrlCreator.getChecklistManagementList(companyId), { params });

    getChecklistManagementDetails = async (
        companyId: number,
        checklistId: number,
    ): Promise<AxiosResponse<IChecklistManagementResource>> =>
        axiosInstance.get(UrlCreator.getChecklistManagementDetails(companyId, checklistId));

    putChecklistManagement = async (
        companyId: number,
        checklistId: number,
        body: IChecklistManagementEditBody,
    ): Promise<AxiosResponse<IChecklistManagementResource>> =>
        axiosInstance.put(UrlCreator.putChecklistManagement(companyId, checklistId), body);

    postChecklistManagementUpload = async (
        companyId: number,
        body: FormData,
    ): Promise<AxiosResponse<IChecklistManagementResource>> =>
        axiosInstance.post(UrlCreator.postChecklistManagementUpload(companyId), body, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });

    deleteChecklistManagement = async (
        companyId: number,
        checklistId: number,
    ): Promise<AxiosResponse<void>> =>
        axiosInstance.delete(UrlCreator.deleteChecklistManagement(companyId, checklistId));

    postChecklistManagementItem = async (
        companyId: number,
        checklistId: number,
    ): Promise<AxiosResponse<IChecklistManagementItemResource>> =>
        axiosInstance.post(UrlCreator.postChecklistManagementItem(companyId, checklistId));

    putChecklistManagementItem = async (
        companyId: number,
        checklistId: number,
        itemUuid: string,
        body: IChecklistManagementItemEditBody,
    ): Promise<AxiosResponse<IChecklistManagementItemResource>> =>
        axiosInstance.put(UrlCreator.putChecklistManagementItem(companyId, checklistId, itemUuid), body);

    deleteChecklistManagementItem = async (
        companyId: number,
        checklistId: number,
        itemUuid: string,
    ): Promise<AxiosResponse<void>> =>
        axiosInstance.delete(UrlCreator.deleteChecklistManagementItem(companyId, checklistId, itemUuid));

    postChecklistManagementApprove = async (
        companyId: number,
        checklistId: number,
    ): Promise<AxiosResponse<void>> =>
        axiosInstance.post(UrlCreator.postChecklistManagementApprove(companyId, checklistId));

    getChecklistManagementItemsCsv = async (
        companyId: number,
        checklistId: number,
    ): Promise<AxiosResponse<string>> =>
        axiosInstance.get(UrlCreator.getChecklistManagementItemsCsv(companyId, checklistId), {
            headers: { accept: 'text/csv' },
        });
}

export default new FetchService();
